import React from "react";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  MenuItem,
  TextField
} from "@mui/material";
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { useKivunovaTranslation } from "@kivunova/kivufrontendcommon";
import AppButton from "../../common/AppButton";
import {
  buildStudentSchema,
  StudentFormValues
} from "../../../Utils/studentSchema";
import { Student } from "./types/student";
import {
  useCreateStudent,
  useUpdateStudent
} from "../../../hooks/api/education/useStudent";
import {
  useAcademicPrograms,
  useAcademicStreams,
  useGrades,
  useStudentCategories
} from "../../../hooks/api/education/useAcademicStructure";

interface StudentFormProps {
  open: boolean;
  onClose: () => void;
  onSaved?: () => void;
  student?: Student | null;
}

const StudentForm: React.FC<StudentFormProps> = ({
  open,
  onClose,
  onSaved,
  student
}) => {
  const { t } = useKivunovaTranslation();
  const createStudent = useCreateStudent();
  const updateStudent = useUpdateStudent();
  const isEdit = Boolean(student?.id);
  const { data: programs = [] } = useAcademicPrograms();
  const { data: studentCategories = [] } = useStudentCategories();

  const {
    control,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { errors, isSubmitting }
  } = useForm<StudentFormValues>({
    resolver: yupResolver(buildStudentSchema(t)),
    defaultValues: {
      studentCode: student?.studentCode ?? "",
      fullName: student?.fullName ?? "",
      programId: student?.programId ?? "",
      gradeId: student?.gradeId ?? "",
      streamId: student?.streamId ?? "",
      studentCategoryId: student?.studentCategoryId ?? "",
      guardianPhone: student?.guardianPhone ?? "",
      guardianEmail: student?.guardianEmail ?? ""
    }
  });

  const selectedProgramId = watch("programId");
  const selectedGradeId = watch("gradeId");
  const { data: grades = [] } = useGrades(selectedProgramId || undefined);
  const { data: streams = [] } = useAcademicStreams(
    selectedGradeId || undefined
  );

  React.useEffect(() => {
    if (open) {
      reset({
        studentCode: student?.studentCode ?? "",
        fullName: student?.fullName ?? "",
        programId: student?.programId ?? "",
        gradeId: student?.gradeId ?? "",
        streamId: student?.streamId ?? "",
        studentCategoryId: student?.studentCategoryId ?? "",
        guardianPhone: student?.guardianPhone ?? "",
        guardianEmail: student?.guardianEmail ?? ""
      });
    }
  }, [open, reset, student]);

  const onSubmit = async (values: StudentFormValues) => {
    const payload = {
      studentCode: values.studentCode,
      fullName: values.fullName,
      programId: values.programId || undefined,
      gradeId: values.gradeId || undefined,
      streamId: values.streamId || undefined,
      studentCategoryId: values.studentCategoryId || undefined,
      guardianPhone: values.guardianPhone || undefined,
      guardianEmail: values.guardianEmail || undefined
    };

    if (isEdit && student?.id) {
      await updateStudent.mutateAsync({
        studentId: student.id,
        updates: payload
      });
    } else {
      await createStudent.mutateAsync(payload);
    }
    onSaved?.();
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>
        {isEdit
          ? t("student-form-edit-title", "Edit student")
          : t("student-form-add-title", "Add student")}
      </DialogTitle>
      <form onSubmit={handleSubmit(onSubmit)}>
        <DialogContent>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 6 }}>
              <Controller
                name="studentCode"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label={t("student-code-label", "Student code")}
                    fullWidth
                    error={Boolean(errors.studentCode)}
                    helperText={errors.studentCode?.message}
                  />
                )}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <Controller
                name="fullName"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label={t("student-name-label", "Full name")}
                    fullWidth
                    error={Boolean(errors.fullName)}
                    helperText={errors.fullName?.message}
                  />
                )}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <Controller
                name="programId"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    select
                    label={t("student-program-label", "Program")}
                    fullWidth
                    onChange={(event) => {
                      field.onChange(event);
                      setValue("gradeId", "");
                      setValue("streamId", "");
                    }}
                  >
                    <MenuItem value="">
                      {t("student-program-none", "None")}
                    </MenuItem>
                    {programs.map((option) => (
                      <MenuItem key={option.id} value={option.id}>
                        {option.name}
                      </MenuItem>
                    ))}
                  </TextField>
                )}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <Controller
                name="gradeId"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    select
                    label={t("student-grade-label", "Grade")}
                    fullWidth
                    disabled={!selectedProgramId}
                    onChange={(event) => {
                      field.onChange(event);
                      setValue("streamId", "");
                    }}
                  >
                    <MenuItem value="">
                      {t("student-grade-none", "None")}
                    </MenuItem>
                    {grades.map((option) => (
                      <MenuItem key={option.id} value={option.id}>
                        {option.name}
                      </MenuItem>
                    ))}
                  </TextField>
                )}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <Controller
                name="streamId"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    select
                    label={t("student-stream-label", "Stream")}
                    fullWidth
                    disabled={!selectedGradeId}
                  >
                    <MenuItem value="">
                      {t("student-stream-none", "None")}
                    </MenuItem>
                    {streams.map((option) => (
                      <MenuItem key={option.id} value={option.id}>
                        {option.name}
                      </MenuItem>
                    ))}
                  </TextField>
                )}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <Controller
                name="studentCategoryId"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    select
                    label={t("student-category-label", "Student category")}
                    fullWidth
                  >
                    <MenuItem value="">
                      {t("student-category-none", "None")}
                    </MenuItem>
                    {studentCategories.map((option) => (
                      <MenuItem key={option.id} value={option.id}>
                        {option.name}
                      </MenuItem>
                    ))}
                  </TextField>
                )}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <Controller
                name="guardianPhone"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label={t("student-guardian-phone-label", "Guardian phone")}
                    fullWidth
                  />
                )}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <Controller
                name="guardianEmail"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label={t("student-guardian-email-label", "Guardian email")}
                    fullWidth
                    error={Boolean(errors.guardianEmail)}
                    helperText={errors.guardianEmail?.message}
                  />
                )}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <AppButton variant="outlined" onClick={onClose}>
            {t("cancel", "Cancel")}
          </AppButton>
          <AppButton type="submit" disabled={isSubmitting}>
            {t("save", "Save")}
          </AppButton>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default StudentForm;
